import React, { MouseEventHandler } from 'react';
import classNames from 'classnames';
import { ButtonType } from 'Douleutaras/components/CustomButton/CustomButton';

export enum HeightStyle {
  DEFAULT = 'default',
  TALL = 'tall'
}

import Tooltip, { TooltipDirection } from '../Tooltip/Tooltip';

import './RegularButton.scss';

interface RegularButtonProps {
  disabled: boolean;
  icon: string;
  title: string | React.ReactElement;
  testid: number;
  onClick: MouseEventHandler;
  size: 'small' | 'large' | 'medium';
  type: ButtonType;
  fullWidth: boolean;
  modifier: 'primary' | 'primary-outline' | 'primary-no-line' | 'success';
  isAnchor: boolean;
  href: string;
  className: string;
  rounded: boolean;
  oneLine?: boolean;
  tooltip?: string;
  tooltipDirection?: TooltipDirection;
  heightStyle?: HeightStyle;
  threeD?: boolean;
}

const RegularButton = ({ disabled = false,
                         icon = '',
                         modifier = 'primary',
                         oneLine = false,
                         title,
                         testid,
                         onClick,
                         size = 'medium',
                         type,
                         fullWidth,
                         isAnchor = false,
                         href = '#',
                         className,
                         rounded,
                         tooltip,
                         tooltipDirection: tooltipDirectionCustom,
                         heightStyle = HeightStyle.DEFAULT,
                         threeD = false
  }: Partial<RegularButtonProps>) => {

  const WrapElement = isAnchor ? 'a' : 'button';
  // Props that may or maynot exist in WrapElement
  const conditionalProps = {
    href: undefined,
    disabled: undefined,
    type: undefined
  };
  if (isAnchor) {
    conditionalProps.href = href;
  } else {
    if (disabled) {
      conditionalProps.disabled = true;
    }
    if (type) {
      conditionalProps.type = type;
    }
  }

  const showTooltip = oneLine || Boolean(tooltip);
  const tooltipText = tooltip || title;
  const tooltipDirection = oneLine ? 'bottom' : 'top';

  return (
    <WrapElement
      {...conditionalProps}
      styleName={
        classNames(['regular-btn', `regular-btn--${size}`], {
          // Modifier
          'regular-btn--primary': modifier === 'primary',
          'regular-btn--success': modifier === 'success',
          'regular-btn--primary-outline': modifier === 'primary-outline',
          'regular-btn--primary-no-line': modifier === 'primary-no-line',
          // State
          'regular-btn--disabled': isAnchor && disabled,
          // Other
          'regular-btn--rounded': rounded,
          'regular-btn--full-width': fullWidth,
          'regular-btn--oneLine': oneLine,
          'regular-btn--tall': heightStyle === HeightStyle.TALL,
          'regular-btn--three-d': threeD
        })}
      className={className}
      type={Boolean(onClick) ? 'button' : 'submit'}
      data-testid={testid} onClick={onClick}
    >
      { icon &&
        <span styleName="regular-btn__icon" className={icon}/>
      }
      { showTooltip
        ? <Tooltip content={tooltipText} direction={tooltipDirectionCustom ?? tooltipDirection}>
            <span styleName="regular-btn__title">{title}</span>
          </Tooltip>
        : <span styleName="regular-btn__title">{title}</span>
      }
    </WrapElement>
  );
};

export default RegularButton;
